import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useWallet } from "@/context/WalletContext";
import { usePolling, useNow } from "@/components/croc/hooks";
import { Panel, SectionTitle, Pill } from "@/components/croc/ui";
import { fmtNum, fmtUsd, pct } from "@/lib/format";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Layers, Clock, Gift, TrendingUp, Plus } from "lucide-react";

export default function StakingTiers() {
  const { address, user, refreshUser } = useWallet();
  const { data: config } = usePolling(() => api.config(), [], 120000);
  const [claiming, setClaiming] = useState(false);
  useNow(1000);

  const tiers = config?.tiers ?? [];
  const stakes = user?.stakes ?? [];

  const claim = async () => {
    try {
      setClaiming(true);
      const res = await api.claim(address);
      toast.success("Rewards claimed", { description: `${fmtUsd(res.claimed)} added to your wallet balance.` });
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nothing to claim");
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div>
      <SectionTitle
        eyebrow="5 Staking Tiers"
        title="Staking Tiers"
        subtitle="Weighting multipliers from 1.0x to 1.75x. Daily rate floats with revenue, capped at 0.5%/day."
        right={
          <button
            data-testid="claim-rewards-button"
            onClick={claim}
            disabled={claiming || (user?.pending_roi ?? 0) <= 0}
            className="flex items-center gap-2 rounded-xl bg-[#10B981] px-4 py-2.5 font-semibold text-[#050806] hover:bg-[#34D399] transition-colors disabled:opacity-50"
          >
            <Gift className="w-4 h-4" /> Claim {fmtUsd(user?.pending_roi)}
          </button>
        }
        testid="tiers-title"
      />

      <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-8">
        {tiers.map((t, i) => (
          <motion.div
            key={t.key}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06 }}
            className="croc-panel p-5 flex flex-col"
            data-testid={`tier-card-${t.key}`}
          >
            <div className="flex items-center justify-between">
              <Pill tone="gold">{t.multiplier}x</Pill>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{t.key}</span>
            </div>
            <div className="font-display font-bold text-white text-lg mt-3">{t.name}</div>
            <div className="text-xs text-muted-foreground">{t.tagline}</div>
            <div className="mt-4 pt-4 border-t border-white/5">
              <div className="font-num font-black text-2xl gold-text">{pct(t.daily_rate * 100)}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-widest">per day (variable)</div>
            </div>
            <div className="mt-3 space-y-1.5 text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">Lock</span><span className="font-num text-white">{t.days}d</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Max Rewards</span><span className="font-num text-white">{pct(t.max_roi * 100, 0)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">5% Bonus</span><span className={`font-num ${t.sponsor_bonus ? "emerald-text" : "text-muted-foreground"}`}>{t.sponsor_bonus ? "Yes" : "No"}</span></div>
            </div>
            <Link
              to="/buy"
              data-testid={`staking-tier-${t.key}-select-button`}
              className="mt-4 text-center rounded-xl border border-[#1CFD4C]/30 py-2 text-sm text-[#1CFD4C] hover:bg-[#1CFD4C]/10 transition-colors"
            >
              Stake in this tier
            </Link>
          </motion.div>
        ))}
      </div>

      <SectionTitle eyebrow="Active Positions" title="Your Stakes" testid="active-stakes-title" />
      {stakes.length === 0 ? (
        <Panel className="text-center py-14" data-testid="no-stakes">
          <Layers className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No active stakes yet.</p>
          <Link to="/buy" className="inline-flex items-center gap-2 mt-4 rounded-xl bg-[#1CFD4C] px-4 py-2.5 font-semibold text-[#050806] hover:bg-[#4DFF74] transition-colors">
            <Plus className="w-4 h-4" /> Create your first stake
          </Link>
        </Panel>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {stakes.map((s) => {
            const start = new Date(s.start).getTime();
            const end = new Date(s.end).getTime();
            const prog = Math.min(100, ((Date.now() - start) / (end - start)) * 100);
            return (
              <Panel key={s.id} data-testid={`active-stake-${s.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-display font-bold text-white">{s.tier_name}</div>
                    <div className="text-xs text-muted-foreground font-num">{fmtNum(s.croc_amount)} CROC · {fmtUsd(s.principal_usd)}</div>
                  </div>
                  <Pill tone={s.active ? "emerald" : "neutral"}>{s.active ? "Active" : "Matured"}</Pill>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <MiniStat icon={TrendingUp} label="Accrued" value={fmtUsd(s.accrued)} />
                  <MiniStat icon={Gift} label="Daily" value={pct(s.daily_rate * 100)} />
                  <MiniStat icon={Clock} label="Ends" value={new Date(s.end).toLocaleDateString()} />
                </div>
                <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#10B981] to-[#1CFD4C]" style={{ width: `${prog}%` }} />
                </div>
                <div className="text-[11px] text-muted-foreground mt-1.5 font-num">{prog.toFixed(1)}% of lock elapsed</div>
              </Panel>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-white/10 bg-[#090E0B] p-2.5">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="font-num font-bold text-sm text-white mt-1">{value}</div>
    </div>
  );
}
